import { motion, AnimatePresence } from 'framer-motion'
import { X, AlertCircle, TrendingUp, Target, Sparkles, CheckCircle, Calendar, Euro } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'

interface DispoDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

const DispoDetailsModal = ({ isOpen, onClose }: DispoDetailsModalProps) => {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              <div className="sticky top-0 z-10 bg-gradient-to-r from-red-600/20 to-orange-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                      <AlertCircle className="h-8 w-8 text-red-400" />
                      {t('dispo.modal.title')}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">{t('dispo.modal.subtitle')}</p>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl p-6 border border-red-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-red-500/20 rounded-xl">
                        <Sparkles className="h-6 w-6 text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('dispo.modal.whatIs')}</h3>
                        <p className="text-gray-300 leading-relaxed">{t('dispo.modal.intro')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Target className="h-6 w-6 text-red-400" />
                    {t('dispo.modal.howItWorks')}
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl p-6 border border-red-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-red-500/20 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-red-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('dispo.modal.step1.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('dispo.modal.step1.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl p-6 border border-orange-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-orange-500/20 rounded-lg">
                          <AlertCircle className="h-6 w-6 text-orange-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('dispo.modal.step2.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('dispo.modal.step2.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/5 rounded-2xl p-6 border border-yellow-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-yellow-500/20 rounded-lg">
                          <Calendar className="h-6 w-6 text-yellow-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('dispo.modal.step3.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('dispo.modal.step3.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/5 rounded-2xl p-6 border border-green-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                          <CheckCircle className="h-6 w-6 text-green-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('dispo.modal.step4.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('dispo.modal.step4.description')}</p>
                    </motion.div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Euro className="h-6 w-6 text-yellow-400" />
                    {t('dispo.modal.whyCostly')}
                  </h3>

                  <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-2xl p-6 border border-red-500/30">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-400 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-white font-semibold mb-1">{t('dispo.modal.cost1.title')}</h4>
                          <p className="text-gray-300 text-sm">{t('dispo.modal.cost1.description')}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-400 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-white font-semibold mb-1">{t('dispo.modal.cost2.title')}</h4>
                          <p className="text-gray-300 text-sm">{t('dispo.modal.cost2.description')}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-400 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="text-white font-semibold mb-1">{t('dispo.modal.cost3.title')}</h4>
                          <p className="text-gray-300 text-sm">{t('dispo.modal.cost3.description')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('dispo.modal.aiHelps')}</h3>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      { icon: TrendingUp, title: t('dispo.modal.feature1.title'), desc: t('dispo.modal.feature1.description') },
                      { icon: Calendar, title: t('dispo.modal.feature2.title'), desc: t('dispo.modal.feature2.description') },
                      { icon: Target, title: t('dispo.modal.feature3.title'), desc: t('dispo.modal.feature3.description') },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl p-6 border border-blue-500/30"
                      >
                        <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center mb-4">
                          <item.icon className="h-6 w-6 text-blue-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 rounded-2xl p-8 border border-red-500/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-3">{t('dispo.modal.cta')}</h3>
                  <p className="text-gray-300 mb-6">{t('dispo.modal.ctaDescription')}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link
                      to="/features/dispo-prevention"
                      className="px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all inline-block"
                    >
                      View Full Page
                    </Link>
                    <button
                      onClick={onClose}
                      className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
                    >
                      {t('dispo.modal.gotIt')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default DispoDetailsModal
